import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLogout, useMe } from '@/api/auth';
import { ApiError } from '@/api/client';
import { useAcceptInvite, useInvitePreview } from '@/api/invites';
import { Button, FullPageSpinner, Input } from '@/components/ui';
import { humanize } from '@/lib/format';
import { AuthLayout } from './AuthLayout';

const schema = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(120),
  password: z.string().min(10, 'Use at least 10 characters').max(128),
});
type Form = z.infer<typeof schema>;

/** /accept-invite?token=… — join a workspace with your own name and password. */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const me = useMe();
  const logout = useLogout();
  const preview = useInvitePreview(token || null);
  const accept = useAcceptInvite(token);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    values: { name: preview.data?.name ?? '', password: '' },
  });

  if (token && preview.isPending) return <FullPageSpinner label="Checking your invite" />;

  if (!token || preview.error) {
    const message =
      preview.error instanceof ApiError ? preview.error.message : 'This invite link is not valid.';
    return (
      <AuthLayout
        title="Invite not available"
        subtitle={message}
        footer={
          <Link to="/login" className="text-brand-600 font-semibold hover:underline">
            Go to sign in
          </Link>
        }
      >
        <p className="text-sm text-slate-600">Ask your admin to send you a new invite.</p>
      </AuthLayout>
    );
  }

  const invite = preview.data!;
  // Someone else is signed in on this browser: they must sign out first.
  if (me.data && me.data.email !== invite.email) {
    return (
      <AuthLayout title={`Join ${invite.workspaceName}`} subtitle={`This invite is for ${invite.email}.`}>
        <p className="mb-4 text-sm text-slate-600">
          You’re signed in as <strong>{me.data.email}</strong>. Sign out to accept it.
        </p>
        <Button className="w-full" loading={logout.isPending} onClick={() => logout.mutate()}>
          Sign out and continue
        </Button>
      </AuthLayout>
    );
  }

  const onSubmit = handleSubmit((values) =>
    accept.mutate(values, { onSuccess: () => navigate('/inbox', { replace: true }) }),
  );

  return (
    <AuthLayout
      title={`Join ${invite.workspaceName}`}
      subtitle={
        <>
          You’re invited as {/^[aeiou]/i.test(invite.role) ? 'an' : 'a'}{' '}
          <strong>{humanize(invite.role)}</strong>. Choose your name and password.
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Input label="Email" value={invite.email} disabled readOnly />
        <Input
          label="Your name"
          autoComplete="name"
          autoFocus
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          hint="At least 10 characters."
          error={errors.password?.message}
          {...register('password')}
        />
        {accept.error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {accept.error.message}
          </p>
        )}
        <Button type="submit" className="w-full" loading={accept.isPending}>
          Join the team
        </Button>
      </form>
    </AuthLayout>
  );
}
